import { motion } from 'framer-motion';
import { ArrowLeft, ExternalLink, Key, Mail } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { feedService } from '@/api/services/feed';
import { Badge, Skeleton } from '@/components/ui';
import { getThemeLabel } from '@/features/feed/utils';
import { FROM_PUBLIC_FEED_KEY } from '@/features/public-feed/components/SignupWallModal';
import { useAuth } from '@/providers';
import type { Content } from '@/types';

// Set flag that user came from public feed
function setFromPublicFeedFlag() {
  sessionStorage.setItem(FROM_PUBLIC_FEED_KEY, 'true');
}

export function PublicContentDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const [content, setContent] = useState<Content | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch content preview (hook must be called before any early returns)
  useEffect(() => {
    // Skip fetch if authenticated (will redirect)
    if (isAuthenticated) return;

    const fetchContentPreview = async () => {
      if (!id) return;

      try {
        setIsLoading(true);
        setError(null);
        const contentData = await feedService.getContentPreview(id);
        setContent(contentData);
      } catch (err) {
        console.error('Failed to fetch content preview:', err);
        setError(t('publicFeed.contentError', 'Erro ao carregar o conteúdo'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchContentPreview();
  }, [id, t, isAuthenticated]);

  // Redirect authenticated users to full content
  if (isAuthenticated) {
    return <Navigate to={`/content/${id}`} replace />;
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F4F2F2]">
        <Header onBack={() => navigate(-1)} />
        <main className="max-w-2xl mx-auto px-4 py-8">
          <Skeleton className="w-20 h-6 mb-4 rounded-full" />
          <Skeleton className="w-full h-12 mb-4" />
          <Skeleton className="w-3/4 h-12 mb-6" />
          <div className="flex gap-2 mb-8">
            <Skeleton className="w-24 h-4" />
            <Skeleton className="w-20 h-4" />
            <Skeleton className="w-32 h-4" />
          </div>
          <Skeleton className="w-full h-4 mb-2" />
          <Skeleton className="w-full h-4 mb-2" />
          <Skeleton className="w-5/6 h-4 mb-4" />
        </main>
      </div>
    );
  }

  // Error state
  if (error || !content) {
    return (
      <div className="min-h-screen bg-[#F4F2F2]">
        <Header onBack={() => navigate(-1)} />
        <main className="max-w-2xl mx-auto px-4 py-8">
          <div className="text-center py-12 text-[#888185]">
            <p>{error || t('publicFeed.contentNotFound', 'Conteúdo não encontrado')}</p>
            <Link to="/" className="text-[#E94E1B] mt-4 inline-block hover:underline">
              {t('common.backToHome', 'Voltar ao início')}
            </Link>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F2F2]">
      <Header onBack={() => navigate(-1)} sourceUrl={content.sourceUrl} />

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Theme badge */}
        <Badge variant="theme" className="mb-4">
          {getThemeLabel(content.theme, t)}
        </Badge>

        {/* Title */}
        <h1 className="font-serif text-3xl md:text-4xl font-semibold text-[#2E2A23] leading-tight mb-4">
          {content.title}
        </h1>

        {/* Metadata */}
        <div className="flex items-center gap-2 text-sm text-[#888185] mb-8">
          <span className="font-medium">{content.author}</span>
          <span>·</span>
          <span>{formatDate(content.publishedAt)}</span>
        </div>

        {/* Summary */}
        <p className="text-[#888185] text-lg leading-relaxed mb-6 border-l-4 border-[#E94E1B] pl-4 italic">
          {content.summary}
        </p>

        {/* Content preview with blur protection */}
        <ContentPreviewWithBlur body={content.body || ''} />

        {/* Inline Signup Wall */}
        <InlineSignupWall />
      </main>
    </div>
  );
}

// Header component
function Header({ onBack, sourceUrl }: { onBack: () => void; sourceUrl?: string }) {
  const { t } = useTranslation();

  return (
    <header className="sticky top-0 z-40 bg-white border-b border-[#CAC7C7]">
      <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-2 text-[#888185] hover:text-[#2E2A23] transition-colors"
        >
          <ArrowLeft size={20} />
          <span className="text-sm font-medium">{t('common.back', 'Voltar')}</span>
        </button>

        {sourceUrl && (
          <a
            href={sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-[#888185] hover:text-[#E94E1B] transition-colors text-sm"
          >
            <span>{t('content.viewOriginal', 'Ver original')}</span>
            <ExternalLink size={16} />
          </a>
        )}
      </div>
    </header>
  );
}

// Content preview with blur protection
// Security: Backend only sends truncated content (preview)
// This component shows preview text clearly, then blurred placeholder content
function ContentPreviewWithBlur({ body }: { body: string }) {
  const contentRef = useRef<HTMLDivElement>(null);

  // Prevent right-click context menu on content
  useEffect(() => {
    const element = contentRef.current;
    if (!element) return;

    const preventContextMenu = (e: MouseEvent) => {
      e.preventDefault();
    };

    element.addEventListener('contextmenu', preventContextMenu);
    return () => element.removeEventListener('contextmenu', preventContextMenu);
  }, []);

  // Prevent text selection via keyboard shortcuts
  useEffect(() => {
    const element = contentRef.current;
    if (!element) return;

    const preventSelection = (e: KeyboardEvent) => {
      // Prevent Ctrl+A, Ctrl+C
      if (e.ctrlKey && (e.key === 'a' || e.key === 'c' || e.key === 'A' || e.key === 'C')) {
        e.preventDefault();
      }
    };

    element.addEventListener('keydown', preventSelection);
    return () => element.removeEventListener('keydown', preventSelection);
  }, []);

  // Detect devtools and add warning (non-blocking)
  useEffect(() => {
    const detectDevTools = () => {
      const threshold = 160;
      const widthThreshold = window.outerWidth - window.innerWidth > threshold;
      const heightThreshold = window.outerHeight - window.innerHeight > threshold;

      if (widthThreshold || heightThreshold) {
        console.log(
          '%c⚠️ Conteúdo protegido',
          'color: #E94E1B; font-size: 20px; font-weight: bold;',
        );
        console.log(
          '%cEste conteúdo é apenas uma prévia. Crie uma conta gratuita para acessar o conteúdo completo.',
          'color: #888185; font-size: 14px;',
        );
      }
    };

    detectDevTools();
    window.addEventListener('resize', detectDevTools);
    return () => window.removeEventListener('resize', detectDevTools);
  }, []);

  return (
    <motion.div
      ref={contentRef}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="relative"
      tabIndex={-1}
    >
      {/* Preview Content (first 2-3 lines visible) */}
      <div className="relative">
        <div className="relative max-h-[120px] overflow-hidden">
          <div
            className="font-serif text-[17px] md:text-[18px] leading-[1.8] text-[#2E2A23] select-none"
            style={{ userSelect: 'none', WebkitUserSelect: 'none' }}
            dangerouslySetInnerHTML={{ __html: body }}
          />

          {/* Gradient fade */}
          <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-b from-transparent via-[rgba(244,242,242,0.8)] to-[#F4F2F2] pointer-events-none" />
        </div>

        {/* Blurred placeholder content */}
        <div className="mt-4 select-none pointer-events-none">
          <div className="blur-[12px] opacity-50 space-y-4">
            <div className="font-serif text-[18px] leading-[1.8] text-[#2E2A23]">
              <p>
                Lorem ipsum dolor sit amet, consectetur adipiscing elit. Sed do eiusmod tempor
                incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud
                exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.
              </p>
              <p>
                Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu
                fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa
                qui officia deserunt mollit anim id est laborum.
              </p>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// Inline Signup Wall component
function InlineSignupWall() {
  const { t } = useTranslation();

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.3 }}
      className="bg-white border border-[#CAC7C7] rounded-xl p-8 mt-8"
    >
      {/* Headline */}
      <h3 className="font-serif text-xl font-semibold text-[#2E2A23] mb-2">
        {t('publicFeed.inlineWall.title', 'Continue lendo')}
      </h3>

      {/* Subheadline */}
      <p className="text-[14px] leading-[1.5] text-[#888185] mb-6">
        {t(
          'publicFeed.inlineWall.description',
          'Crie sua conta gratuita para acessar o conteúdo completo e salvar para depois.',
        )}
      </p>

      {/* Social Login Buttons - Grid 2x2 */}
      <div className="grid grid-cols-2 gap-2.5 mb-5">
        <SocialIconButton provider="google" />
        <SocialIconButton provider="apple" />
        <SocialIconButton provider="microsoft" />
        <SocialIconButton provider="linkedin" />
      </div>

      {/* Divider */}
      <div className="flex items-center gap-3 my-5">
        <div className="flex-1 h-px bg-[#CAC7C7]" />
        <span className="text-[13px] text-[#888185]">{t('publicFeed.inlineWall.or', 'ou')}</span>
        <div className="flex-1 h-px bg-[#CAC7C7]" />
      </div>

      {/* Magic Link Button */}
      <Link
        to="/login"
        onClick={setFromPublicFeedFlag}
        className="w-full h-11 bg-[#2E2A23] text-[#F4F2F2] rounded-lg text-[14px] font-medium hover:bg-[#3D3833] transition-all hover:-translate-y-px flex items-center justify-center gap-2"
      >
        <Mail className="w-5 h-5" />
        {t('publicFeed.inlineWall.magicLink', 'Entrar com magic link')}
      </Link>

      {/* Password Login Button */}
      <Link
        to="/login"
        onClick={setFromPublicFeedFlag}
        className="w-full h-11 mt-3 bg-white border border-[#CAC7C7] text-[#2E2A23] rounded-lg text-[14px] font-medium hover:bg-[#F4F2F2] hover:border-[#888185] transition-all flex items-center justify-center gap-2"
      >
        <Key className="w-5 h-5" />
        {t('publicFeed.inlineWall.password', 'Entrar com senha')}
      </Link>

      {/* Login Link */}
      <p className="text-center text-[14px] text-[#888185] mt-4">
        {t('publicFeed.inlineWall.hasAccount', 'Já tem conta?')}{' '}
        <Link
          to="/login"
          onClick={setFromPublicFeedFlag}
          className="text-[#E94E1B] hover:underline font-medium"
        >
          {t('publicFeed.inlineWall.login', 'Entrar')}
        </Link>
      </p>
    </motion.div>
  );
}

// Social Icon Button Component for Inline Signup Wall
interface SocialIconButtonProps {
  provider: 'google' | 'apple' | 'microsoft' | 'linkedin';
}

function SocialIconButton({ provider }: SocialIconButtonProps) {
  const config = {
    google: {
      label: 'Google',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
          <path
            fill="#4285F4"
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
          />
          <path
            fill="#34A853"
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
          />
          <path
            fill="#FBBC05"
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
          />
          <path
            fill="#EA4335"
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
          />
        </svg>
      ),
    },
    apple: {
      label: 'Apple',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#000000" aria-hidden="true">
          <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
        </svg>
      ),
    },
    microsoft: {
      label: 'Microsoft',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
          <path fill="#F25022" d="M1 1h10v10H1z" />
          <path fill="#00A4EF" d="M1 13h10v10H1z" />
          <path fill="#7FBA00" d="M13 1h10v10H13z" />
          <path fill="#FFB900" d="M13 13h10v10H13z" />
        </svg>
      ),
    },
    linkedin: {
      label: 'LinkedIn',
      icon: (
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#0A66C2" aria-hidden="true">
          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
        </svg>
      ),
    },
  };

  const { label, icon } = config[provider];

  if (provider === 'linkedin') {
    return (
      <button
        type="button"
        disabled
        className="h-11 bg-white border border-[#CAC7C7] rounded-lg flex items-center justify-center gap-2 opacity-50 cursor-not-allowed"
        aria-label={`Continuar com ${label}`}
      >
        {icon}
        <span className="text-[13px] font-medium text-[#2E2A23]">{label}</span>
      </button>
    );
  }

  return (
    <Link
      to="/login"
      onClick={setFromPublicFeedFlag}
      className="h-11 bg-white border border-[#CAC7C7] rounded-lg hover:bg-[#F4F2F2] hover:border-[#888185] transition-all flex items-center justify-center gap-2"
      aria-label={`Continuar com ${label}`}
    >
      {icon}
      <span className="text-[13px] font-medium text-[#2E2A23]">{label}</span>
    </Link>
  );
}
